import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useFirestore } from '../../contexts/FirestoreContext';
import { collection, query, where, orderBy, onSnapshot, addDoc, serverTimestamp, getDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, getStorage, deleteObject } from 'firebase/storage';
import { toast } from 'react-toastify';
import { Button, Card, Upload, Tag, Empty, Space, Modal, Form, Input, Select, Table, Badge, Tooltip, Popconfirm, Divider, Progress } from 'antd';
import { UploadOutlined, FileOutlined, FileImageOutlined, FileWordOutlined, FileExcelOutlined, FilePdfOutlined, HistoryOutlined, EditOutlined, DeleteOutlined, DownloadOutlined, EyeOutlined, TagOutlined, UserOutlined, ClockCircleOutlined, CloseOutlined, FullscreenOutlined, FilePptOutlined } from '@ant-design/icons';
import { createMessageAuthorName } from '../../utils/userDisplayUtils';
import { createIncidentLog, getUserFullName, ACTIVITY_TYPES } from '../../utils/activityLogger';

const { Option } = Select;
const { TextArea } = Input;

const LinkedDocsTab = ({ incidentId }) => {
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [userOrgId, setUserOrgId] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('all');

  // Document viewer modal state
  const [isViewerModalVisible, setIsViewerModalVisible] = useState(false);
  const [viewingDocument, setViewingDocument] = useState(null);

  const documentTypes = [
    { id: 'report', name: 'Reports', color: 'blue' },
    { id: 'policy', name: 'Policies', color: 'green' },
    { id: 'procedure', name: 'Procedures', color: 'purple' },
    { id: 'evidence', name: 'Evidence', color: 'orange' },
    { id: 'other', name: 'Other', color: 'default' }
  ];

  const documentStatuses = [
    { id: 'draft', name: 'Draft', color: 'default' },
    { id: 'review', name: 'Under Review', color: 'processing' },
    { id: 'approved', name: 'Approved', color: 'success' },
    { id: 'archived', name: 'Archived', color: 'warning' }
  ];

  useEffect(() => {
    // Get user's orgId
    const fetchUserOrgId = async () => {
      try {
        const userDocRef = doc(db, 'c3_users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          setUserOrgId(userDoc.data().orgId);
        }
      } catch (error) {
        console.error('Error fetching user orgId:', error);
      }
    };
    fetchUserOrgId();
  }, [currentUser.uid, db]);

  useEffect(() => {
    if (!incidentId || !userOrgId) return;

    const docsRef = collection(db, 'c3_linked_documents');
    const q = query(
      docsRef,
      where('incidentId', '==', incidentId),
      where('orgId', '==', userOrgId),
      orderBy('uploadedAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const docsList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setDocuments(docsList);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching documents:', error);
        setError('Failed to load documents');
        setLoading(false);
        toast.error('Failed to load documents');
      }
    );

    return () => unsubscribe();
  }, [incidentId, userOrgId, db]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate prerequisites
    if (!userOrgId) {
      toast.error('User organization not found. Please refresh and try again.');
      return;
    }

    if (!incidentId) {
      toast.error('Incident ID not found. Please refresh and try again.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      // Get proper user name from database
      let uploaderName = 'Unknown User';
      try {
        const userDocRef = doc(db, 'c3_users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          // Use the same utility function as the live chat
          uploaderName = createMessageAuthorName(userData);
        }
      } catch (error) {
        console.error('Error fetching user data for uploader name:', error);
        uploaderName = currentUser.displayName || 'Unknown User';
      }

      // Upload file to Firebase Storage
      const storage = getStorage();
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const storageRef = ref(storage, `incident-documents/${incidentId}/${fileName}`);
      
      console.log('Uploading file to Firebase Storage...');
      setUploadProgress(25);
      
      // Upload the file
      const uploadResult = await uploadBytes(storageRef, file);
      console.log('File uploaded successfully:', uploadResult);
      setUploadProgress(50);
      
      // Get the download URL
      const downloadURL = await getDownloadURL(uploadResult.ref);
      console.log('Download URL obtained:', downloadURL);
      setUploadProgress(75);

      // Save document metadata to Firestore
      const docsRef = collection(db, 'c3_linked_documents');
      const uploadTime = new Date();
      const docData = {
        incidentId,
        orgId: userOrgId,
        name: file.name,
        type: file.type,
        size: file.size,
        uploadedBy: currentUser.uid,
        uploadedByName: uploaderName,
        uploadedAt: serverTimestamp(),
        status: 'draft',
        url: downloadURL, // Real Firebase Storage URL
        documentType: 'other',
        version: 1,
        storagePath: `incident-documents/${incidentId}/${fileName}`, // Store path for deletion
        versions: [{
          version: 1,
          name: file.name,
          type: file.type,
          size: file.size,
          uploadedBy: currentUser.uid,
          uploadedByName: uploaderName,
          uploadedAt: uploadTime,
          url: downloadURL, // Real Firebase Storage URL
          storagePath: `incident-documents/${incidentId}/${fileName}`
        }],
        metadata: {
          description: '',
          tags: [],
          confidentiality: 'internal',
          retention: 'standard'
        }
      };

      await addDoc(docsRef, docData);
      setUploadProgress(100);
      
      // FORENSIC LOGGING: Log document upload
      try {
        const userFullName = await getUserFullName(db, currentUser.uid, currentUser.email);
        await createIncidentLog({
          incidentId,
          orgId: userOrgId,
          action: ACTIVITY_TYPES.DOCUMENT_UPLOADED,
          details: `Document uploaded: "${file.name}" (${formatFileSize(file.size)})`,
          userId: currentUser.uid,
          userEmail: currentUser.email,
          userFullName,
          priority: 'normal',
          metadata: {
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            downloadURL: downloadURL,
            storagePath: `incident-documents/${incidentId}/${fileName}`,
            documentType: 'other',
            version: 1
          }
        });
      } catch (logError) {
        console.error('Failed to log document upload:', logError);
        // Don't fail upload if logging fails
      }
      
      console.log('Document metadata saved to Firestore');
      toast.success(`File "${file.name}" uploaded successfully!`);
      
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(`Failed to upload file: ${error.message}`);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleUpdateMetadata = async (values) => {
    try {
      const docRef = doc(db, 'c3_linked_documents', selectedDocument.id);
      await updateDoc(docRef, {
        metadata: {
          ...selectedDocument.metadata,
          ...values
        },
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid
      });
      setIsModalVisible(false);
      form.resetFields();
      toast.success('Document metadata updated successfully');
    } catch (error) {
      console.error('Error updating document metadata:', error);
      toast.error('Failed to update document metadata');
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      // Get document data before deletion for logging
      const docData = documents.find(d => d.id === docId);
      
      const storage = getStorage();
      const storageRef = ref(storage, `incident-documents/${incidentId}/${docId}`);
      
      // Delete file from storage
      await deleteObject(storageRef);
      
      // Delete document from Firestore
      const docRef = doc(db, 'c3_linked_documents', docId);
      await deleteDoc(docRef);
      
      // FORENSIC LOGGING: Log document deletion
      try {
        const userFullName = await getUserFullName(db, currentUser.uid, currentUser.email);
        await createIncidentLog({
          incidentId,
          orgId: userOrgId,
          action: ACTIVITY_TYPES.DOCUMENT_DELETED,
          details: `Document deleted: "${docData?.name || 'Unknown Document'}"`,
          userId: currentUser.uid,
          userEmail: currentUser.email,
          userFullName,
          priority: 'high', // Document deletion is high priority for audit
          metadata: {
            documentId: docId,
            fileName: docData?.name,
            fileType: docData?.type,
            fileSize: docData?.size,
            storagePath: docData?.storagePath,
            documentType: docData?.documentType,
            version: docData?.version
          }
        });
      } catch (logError) {
        console.error('Failed to log document deletion:', logError);
        // Don't fail deletion if logging fails
      }
      
      toast.success('Document deleted successfully');
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    }
  };

  const handleUpdateStatus = async (docId, newStatus) => {
    try {
      const document = documents.find(d => d.id === docId);
      const oldStatus = document?.status;
      
      const docRef = doc(db, 'c3_linked_documents', docId);
      await updateDoc(docRef, {
        status: newStatus,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid
      });
      
      // FORENSIC LOGGING: Log document status change
      try {
        const userFullName = await getUserFullName(db, currentUser.uid, currentUser.email);
        await createIncidentLog({
          incidentId,
          orgId: userOrgId,
          action: ACTIVITY_TYPES.DOCUMENT_STATUS_CHANGED,
          details: `Document status changed: "${document?.name || 'Unknown Document'}" from "${oldStatus}" to "${newStatus}"`,
          userId: currentUser.uid,
          userEmail: currentUser.email,
          userFullName,
          priority: 'normal',
          metadata: {
            documentId: docId,
            fileName: document?.name,
            oldStatus: oldStatus,
            newStatus: newStatus,
            changeType: 'status'
          }
        });
      } catch (logError) {
        console.error('Failed to log document status change:', logError);
        // Don't fail status update if logging fails
      }
      
      toast.success('Document status updated successfully');
    } catch (error) {
      console.error('Error updating document status:', error);
      toast.error('Failed to update document status');
    }
  };

  const getFileIcon = (type) => {
    if (type.includes('pdf')) return <FilePdfOutlined />;
    if (type.includes('image')) return <FileImageOutlined />;
    if (type.includes('word') || type.includes('doc')) return <FileWordOutlined />;
    if (type.includes('excel') || type.includes('spreadsheet') || type.includes('sheet') || type.includes('xls')) return <FileExcelOutlined />;
    if (type.includes('powerpoint') || type.includes('presentation') || type.includes('ppt')) return <FilePptOutlined />;
    return <FileOutlined />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileTypeFromName = (fileName, mimeType) => {
    if (!fileName) return 'Unknown';
    
    const extension = fileName.toLowerCase().split('.').pop();
    
    switch (extension) {
      case 'pdf':
        return 'PDF Document';
      case 'doc':
      case 'docx':
        return 'Word Document';
      case 'xls':
      case 'xlsx':
        return 'Excel Spreadsheet';
      case 'ppt':
      case 'pptx':
        return 'PowerPoint Presentation';
      case 'jpg':
      case 'jpeg':
        return 'JPEG Image';
      case 'png':
        return 'PNG Image';
      case 'gif':
        return 'GIF Image';
      case 'txt':
        return 'Text File';
      case 'csv':
        return 'CSV File';
      case 'zip':
        return 'ZIP Archive';
      case 'mp4':
        return 'MP4 Video';
      case 'mp3':
        return 'MP3 Audio';
      default:
        return extension ? `${extension.toUpperCase()} File` : 'Unknown File';
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <span className="fs-4">{getFileIcon(record.type)}</span>
          <div>
            <div>{text}</div>
            <small className="text-muted">
              v{record.version} • {formatFileSize(record.size)}
            </small>
          </div>
        </Space>
      )
    },
    {
      title: 'Type',
      dataIndex: 'name',
      key: 'fileType',
      render: (fileName, record) => {
        const fileType = getFileTypeFromName(fileName, record.type);
        return (
          <Tag color="blue">
            {fileType}
          </Tag>
        );
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        const docStatus = documentStatuses.find(s => s.id === status);
        return (
          <Space>
            <Badge 
              status={docStatus?.color || 'default'} 
              text={docStatus?.name || status}
            />
            <Select
              size="small"
              value={status}
              onChange={(newStatus) => handleUpdateStatus(record.id, newStatus)}
              style={{ width: 120 }}
            >
              {documentStatuses.map(s => (
                <Option key={s.id} value={s.id}>
                  {s.name}
                </Option>
              ))}
            </Select>
          </Space>
        );
      }
    },
    {
      title: 'Uploaded',
      dataIndex: 'uploadedAt',
      key: 'uploadedAt',
      render: (timestamp, record) => (
        <Space direction="vertical" size="small">
          <div>
            <UserOutlined className="me-1" />
            {record.uploadedByName || 'Unknown User'}
          </div>
          <div>
            <ClockCircleOutlined className="me-1" />
            {timestamp?.toDate ? timestamp.toDate().toLocaleString() : 'Just now'}
          </div>
        </Space>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => {
              console.log('View button clicked for:', record);
              console.log('Record URL:', record.url);
              if (record.url && record.url !== 'placeholder-url' && record.url.startsWith('https://')) {
                // Real file - open in viewer modal
                setViewingDocument(record);
                setIsViewerModalVisible(true);
              } else {
                // Placeholder or no URL - show modal with file info
                Modal.info({
                  title: `File Preview: ${record.name}`,
                  content: (
                    <div>
                      <p><strong>File Name:</strong> {record.name}</p>
                      <p><strong>Size:</strong> {formatFileSize(record.size)}</p>
                      <p><strong>Type:</strong> {record.type}</p>
                      <p><strong>Uploaded By:</strong> {record.uploadedByName || 'Unknown User'}</p>
                      <p><strong>Status:</strong> {record.status}</p>
                      <p><em>Note: This file doesn't have a valid download URL. Please re-upload the file.</em></p>
                    </div>
                  ),
                  width: 500
                });
              }
            }}
          >
            View
          </Button>
          <Button
            type="link"
            icon={<DownloadOutlined />}
            onClick={() => {
              console.log('Download button clicked for:', record);
              console.log('Record URL:', record.url);
              if (record.url && record.url !== 'placeholder-url' && record.url.startsWith('https://')) {
                // Real file - trigger download
                const link = document.createElement('a');
                link.href = record.url;
                link.download = record.name;
                link.target = '_blank'; // Open in new tab to avoid potential issues
                document.body.appendChild(link);
                link.click();
              } else {
                // Placeholder or no URL - show info message
                Modal.info({
                  title: 'Download Not Available',
                  content: (
                    <div>
                      <p>This file doesn't have a valid download URL. Please re-upload the file.</p>
                      <p><strong>File:</strong> {record.name}</p>
                      <p><strong>Size:</strong> {formatFileSize(record.size)}</p>
                      <p><strong>URL:</strong> {record.url || 'No URL'}</p>
                    </div>
                  )
                });
              }
            }}
          >
            Download
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => {
              setSelectedDocument(record);
              // Set form values properly
              form.setFieldsValue({
                description: record.metadata?.description || '',
                tags: record.metadata?.tags || [],
                confidentiality: record.metadata?.confidentiality || 'internal',
                retention: record.metadata?.retention || 'standard'
              });
              setIsModalVisible(true);
            }}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this document?"
            onConfirm={() => handleDeleteDocument(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading documents...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  const filteredDocuments = documents.filter(doc => {
    if (activeTab === 'all') return true;
    return doc.documentType === activeTab;
  });

  // Helper functions for document viewing
  const getFileTypeDisplay = (fileType) => {
    if (!fileType) return 'Unknown';
    
    const type = fileType.toLowerCase();
    if (type.includes('pdf')) return 'PDF Document';
    if (type.includes('word') || type.includes('doc')) return 'Word Document';
    if (type.includes('excel') || type.includes('sheet') || type.includes('xls')) return 'Excel Spreadsheet';
    if (type.includes('powerpoint') || type.includes('presentation') || type.includes('ppt')) return 'PowerPoint Presentation';
    return fileType;
  };

  const canPreviewInBrowser = (fileType) => {
    if (!fileType) return false;
    const type = fileType.toLowerCase();
    return type.includes('pdf');
  };

  const isOfficeFile = (fileType) => {
    if (!fileType) return false;
    const type = fileType.toLowerCase();
    return type.includes('word') || type.includes('doc') || 
           type.includes('excel') || type.includes('sheet') || type.includes('xls') ||
           type.includes('powerpoint') || type.includes('presentation') || type.includes('ppt');
  };

  const getOfficeViewerUrl = (fileUrl, fileType) => {
    if (!fileType || !fileUrl) return null;
    
    const type = fileType.toLowerCase();
    if (type.includes('word') || type.includes('doc') || 
        type.includes('excel') || type.includes('sheet') || type.includes('xls') ||
        type.includes('powerpoint') || type.includes('presentation') || type.includes('ppt')) {
      
      console.log('🔗 Attempting Office Online viewer with URL:', fileUrl);
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;
    }
    return null;
  };

  const getGoogleDocsViewerUrl = (fileUrl, fileType) => {
    if (!fileType || !fileUrl) return null;
    
    const type = fileType.toLowerCase();
    if (type.includes('word') || type.includes('doc') || 
        type.includes('excel') || type.includes('sheet') || type.includes('xls') ||
        type.includes('powerpoint') || type.includes('presentation') || type.includes('ppt')) {
      
      console.log('📄 Attempting Google Docs viewer with URL:', fileUrl);
      return `https://docs.google.com/gview?url=${encodeURIComponent(fileUrl)}&embedded=true`;
    }
    return null;
  };



  // Display interface for Office files and other non-previewable documents
  const handleOfficeViewerError = (document) => {
    const isOffice = isOfficeFile(document.type);
    
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center',
        backgroundColor: isOffice ? '#f6ffed' : '#f0f8ff',
        border: `2px solid ${isOffice ? '#b7eb8f' : '#91d5ff'}`,
        borderRadius: '12px',
        margin: '20px',
        minHeight: '400px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{ fontSize: '4em', marginBottom: '24px' }}>
          {getFileIcon(document.type)}
        </div>
        
        <h3 style={{ 
          color: isOffice ? '#389e0d' : '#1890ff', 
          marginBottom: '16px',
          fontSize: '24px'
        }}>
          {isOffice ? `${getFileTypeDisplay(document.type)} Ready` : 'File Ready for Download'}
        </h3>
        
        <h5 style={{ marginBottom: '20px', fontWeight: 'normal' }}>
          {document.name}
        </h5>
        
        <p style={{ marginBottom: '24px', color: '#666', fontSize: '16px', maxWidth: '500px' }}>
          {isOffice ? 
            `This ${getFileTypeDisplay(document.type)} is securely stored and will open directly in your installed ${
              document.type?.toLowerCase().includes('excel') ? 'Microsoft Excel' : 
              document.type?.toLowerCase().includes('word') ? 'Microsoft Word' : 
              document.type?.toLowerCase().includes('powerpoint') ? 'Microsoft PowerPoint' : 
              'Microsoft Office'
            } application when you click the button below.` :
            'This file is ready for download. Click the download button below to save it to your device.'
          }
        </p>

        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '20px' }}>
          <button 
            onClick={() => {
              const link = document.createElement('a');
              link.href = document.url;
              link.download = document.name;
              link.target = '_blank';
              
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
            style={{
              background: '#52c41a',
              border: 'none',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            📥 Download File
          </button>
          
          <button 
            onClick={() => {
              window.open(document.url, '_blank');
            }}
            style={{
              background: '#1890ff',
              border: 'none',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            🔗 Open in New Tab
          </button>
        </div>

        <div style={{ 
          background: 'white', 
          padding: '16px', 
          borderRadius: '8px', 
          border: '1px solid #d9d9d9',
          maxWidth: '400px'
        }}>
          <h6 style={{ margin: '0 0 8px 0', color: '#333' }}>File Information</h6>
          <div style={{ fontSize: '14px', color: '#666', textAlign: 'left' }}>
            <div><strong>Type:</strong> {getFileTypeDisplay(document.type)}</div>
            <div><strong>Size:</strong> {formatFileSize(document.size)}</div>
            <div><strong>Uploaded:</strong> {document.uploadedAt?.toDate ? 
              document.uploadedAt.toDate().toLocaleDateString() : 'Recently'}</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="linked-docs-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="h5 mb-0">Linked Documents</h3>
        <Space>
          <Select
            value={activeTab}
            onChange={setActiveTab}
            style={{ width: 150 }}
          >
            <Option value="all">All Documents</Option>
            {documentTypes.map(type => (
              <Option key={type.id} value={type.id}>
                {type.name}
              </Option>
            ))}
          </Select>
          <Upload
            customRequest={({ file }) => handleFileUpload({ target: { files: [file] } })}
            showUploadList={false}
          >
            <Button
              type="primary"
              icon={<UploadOutlined />}
              loading={isUploading}
              style={{ backgroundColor: '#39325b', borderColor: '#39325b' }}
            >
              {isUploading ? `Uploading... ${uploadProgress}%` : 'Upload Document'}
            </Button>
          </Upload>
          {isUploading && uploadProgress > 0 && (
            <Progress 
              percent={uploadProgress} 
              size="small" 
              style={{ width: '200px', marginLeft: '8px' }}
            />
          )}
        </Space>
      </div>

      {filteredDocuments.length === 0 ? (
        <Empty
          description="No documents linked to this incident"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <Table
          columns={columns}
          dataSource={filteredDocuments}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      )}

      <Modal
        title={`Edit Document Metadata - ${selectedDocument?.name || 'Document'}`}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedDocument(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        {selectedDocument && (
          <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
            <Space>
              <span style={{ fontSize: '1.2em' }}>{getFileIcon(selectedDocument.type)}</span>
              <div>
                <strong>{selectedDocument.name}</strong>
                <br />
                <small className="text-muted">
                  v{selectedDocument.version} • {formatFileSize(selectedDocument.size)} • 
                  Uploaded by {selectedDocument.uploadedByName || 'Unknown User'}
                </small>
              </div>
            </Space>
          </div>
        )}
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdateMetadata}
        >
          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea rows={3} placeholder="Enter document description" />
          </Form.Item>

          <Form.Item
            name="tags"
            label="Tags"
          >
            <Select
              mode="tags"
              placeholder="Add tags"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="confidentiality"
            label="Confidentiality Level"
          >
            <Select placeholder="Select confidentiality level">
              <Option value="public">Public</Option>
              <Option value="internal">Internal</Option>
              <Option value="confidential">Confidential</Option>
              <Option value="restricted">Restricted</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="retention"
            label="Retention Policy"
          >
            <Select placeholder="Select retention policy">
              <Option value="standard">Standard (7 years)</Option>
              <Option value="extended">Extended (10 years)</Option>
              <Option value="permanent">Permanent</Option>
              <Option value="custom">Custom</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                style={{ backgroundColor: '#39325b', borderColor: '#39325b' }}
              >
                Update Metadata
              </Button>
              <Button onClick={() => setIsModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Document Viewer Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '1.2em' }}>
                {viewingDocument && getFileIcon(viewingDocument.type)}
              </span>
              <div>
                <div style={{ fontWeight: 'bold' }}>{viewingDocument?.name}</div>
                <small style={{ color: '#666', fontWeight: 'normal' }}>
                  {viewingDocument && getFileTypeDisplay(viewingDocument.type)} • {formatFileSize(viewingDocument?.size || 0)} • 
                  Version {viewingDocument?.version} • 
                  Uploaded by {viewingDocument?.uploadedByName || 'Unknown User'}
                </small>
              </div>
            </div>
            <Space>
              <Button
                type="text"
                icon={<DownloadOutlined />}
                onClick={() => {
                  if (viewingDocument?.url && viewingDocument.url !== 'placeholder-url' && viewingDocument.url.startsWith('https://')) {
                    const link = document.createElement('a');
                    link.href = viewingDocument.url;
                    link.download = viewingDocument.name;
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }
                }}
                title="Download"
              />
              <Button
                type="text"
                icon={<FullscreenOutlined />}
                onClick={() => {
                  if (viewingDocument?.url && viewingDocument.url !== 'placeholder-url' && viewingDocument.url.startsWith('https://')) {
                    window.open(viewingDocument.url, '_blank');
                  }
                }}
                title="Open in new tab"
              />
            </Space>
          </div>
        }
        open={isViewerModalVisible}
        onCancel={() => {
          setIsViewerModalVisible(false);
          setViewingDocument(null);
        }}
        footer={null}
        width="90vw"
        style={{ top: 20 }}
        styles={{ body: { height: 'calc(90vh - 110px)', padding: 0 } }}
        closeIcon={<CloseOutlined />}
      >
        {viewingDocument && (
          <div style={{ height: '100%', backgroundColor: 'white' }}>
            {(() => {
              const isPdfPreviewable = canPreviewInBrowser(viewingDocument.type);
              const officeViewerUrl = getOfficeViewerUrl(viewingDocument.url, viewingDocument.type);
              const canPreview = isPdfPreviewable || officeViewerUrl;

              if (canPreview) {
                return (
                  <div style={{ height: '100%', position: 'relative' }}>
                    <iframe
                      src={isPdfPreviewable ? 
                        `${viewingDocument.url}#toolbar=0&navpanes=0` : 
                        officeViewerUrl
                      }
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        border: 'none',
                        backgroundColor: 'white',
                        display: 'block'
                      }}
                      title={`${viewingDocument.name} Preview`}
                      onLoad={(e) => {
                        // Enhanced error detection for Office Online viewer
                        if (isOfficeFile(viewingDocument.type)) {
                          setTimeout(() => {
                            try {
                              const iframe = e.target;
                              // Check if the iframe loaded successfully by examining dimensions and content
                              if (iframe.contentWindow && iframe.offsetHeight > 0) {
                                console.log('Office viewer loaded successfully');
                              } else {
                                console.warn('Office viewer may have failed to load properly');
                              }
                            } catch (error) {
                              console.log('Cross-origin iframe, cannot check content - this is normal');
                            }
                          }, 2000); // Give Office Online time to load
                        }
                      }}
                      onError={(e) => {
                        console.error('Error loading document in viewer:', e);
                        // Could trigger fallback here if needed
                      }}
                    />
                    {isOfficeFile(viewingDocument.type) && (
                      <div style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        background: 'rgba(0,0,0,0.8)',
                        color: 'white',
                        padding: '10px 15px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        zIndex: 1000,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                      }}>
                        <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>
                          📄 Office File Viewer
                        </div>
                        <div style={{ marginBottom: '8px' }}>
                          If preview doesn't load properly:
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button 
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = viewingDocument.url;
                              link.download = viewingDocument.name;
                              link.target = '_blank';
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }}
                            style={{
                              background: '#4CAF50',
                              border: 'none',
                              color: 'white',
                              padding: '4px 8px',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            📥 Download
                          </button>
                          <button 
                            onClick={() => {
                              window.open(viewingDocument.url, '_blank');
                            }}
                            style={{
                              background: '#2196F3',
                              border: 'none',
                              color: 'white',
                              padding: '4px 8px',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            🔗 Open Direct
                          </button>

                        </div>
                      </div>
                    )}
                  </div>
                );
              } else {
                return handleOfficeViewerError(viewingDocument);
              }
            })()}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default LinkedDocsTab; 